/**
 * QuickMetrics - "At a glance": where you stand, in the numbers YOU picked.
 *
 * TWO THINGS IT LEARNED from being looked at.
 *
 *   • IT WAS HALF EMPTY. Four fixed tiles in a card that spans half a row can
 *     never fill it: the tile strip was one row tall and the card was two, so
 *     the bottom half was dead space with a divider through it. The strip now
 *     FILLS the card (`h-full` + `auto-rows-fr`), so however many tiles there
 *     are, they share the height rather than leaving a band underneath.
 *   • IT WAS SOMEONE ELSE'S FOUR. Which counters matter is a per-person
 *     question - a partner wants matters and revenue, a paralegal wants tasks
 *     and reviews - and the card had no opinion to offer either of them. The
 *     tile set is now chosen from `metricCatalog` through the gear in the
 *     header, and the choice persists per user through `layoutStore`, the same
 *     store the widget grid itself uses. Nothing new to learn: it is the same
 *     namespace, the same user scoping, the same server sync.
 *
 * Per-tile licence gating stays here, per the registry's split: the widget
 * disappears only when there is not a single tile left, which is the registry's
 * `requires`; which tiles survive is this component's business.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import {
  PiCheckLight,
  PiGaugeLight,
  PiSlidersHorizontalLight,
} from 'react-icons/pi';
import { useLicenseGate } from '../../../hooks/useLicenseGate';
import { DashboardOverview } from '../../../store/reducer/DashboardReducer';
import GlassCard from '../../shared/brand/GlassCard';
import { KpiTile } from '../../shared/brand/Field';
import Skeleton from '../../shared/TailwindComponents/Skeleton';
import useLayoutUserId from '../../../services/layout/useLayoutUserId';
import { readScoped, scopeKey, writeScoped } from '../../../services/layout/layoutStore';
import {
  DEFAULT_GLANCE_KEYS,
  METRIC_CATALOG,
  MetricKey,
  validateMetricKeys,
} from './metricCatalog';

interface QuickMetricsProps {
  overview: DashboardOverview | null;
}

/**
 * Where the choice lives.
 *
 * A `layoutStore` scope rather than a bare `localStorage` key, so it is
 * user-scoped, pruned and synced exactly like the widget arrangement it sits
 * inside. `kind: 'dashboard'` groups it with the grid's own key.
 */
export const glanceScopeKey = (userId: string): string =>
  scopeKey({ kind: 'dashboard', name: 'home-glance', userId });

const QuickMetrics: React.FC<QuickMetricsProps> = ({ overview }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { hasSectionAccess } = useLicenseGate();
  const userId = useLayoutUserId();

  const storageKey = useMemo(() => glanceScopeKey(userId), [userId]);
  const [chosen, setChosen] = useState<MetricKey[]>(() =>
    validateMetricKeys(readScoped(glanceScopeKey(userId)))
  );

  // The real user id arrives with the profile, after the first paint on
  // `anon` - re-read then, or the choice made last session stays invisible.
  const loadedFor = useRef(userId);
  useEffect(() => {
    if (loadedFor.current === userId) return;
    loadedFor.current = userId;
    setChosen(validateMetricKeys(readScoped(glanceScopeKey(userId))));
  }, [userId]);

  const [picking, setPicking] = useState(false);
  const picker = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!picking) return undefined;
    const onDown = (event: MouseEvent) => {
      if (!picker.current?.contains(event.target as Node)) setPicking(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setPicking(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [picking]);

  /** Only what this licence can reach - the picker must not offer a 403. */
  const catalogue = useMemo(
    () => METRIC_CATALOG.filter((metric) => hasSectionAccess(metric.section)),
    [hasSectionAccess]
  );

  const toggle = useCallback(
    (key: MetricKey) => {
      setChosen((current) => {
        const next = current.includes(key)
          ? current.filter((other) => other !== key)
          // Keep catalogue order rather than click order, so the strip does not
          // reshuffle itself every time somebody re-adds a tile.
          : METRIC_CATALOG.filter(
            (metric) => metric.key === key || current.includes(metric.key)
          ).map((metric) => metric.key);
        // The last tile cannot be turned off: an empty At a glance is
        // indistinguishable from a broken one.
        if (next.length === 0) return current;
        writeScoped(storageKey, next);
        return next;
      });
    },
    [storageKey]
  );

  const visibleMetrics = useMemo(() => {
    const picked = catalogue.filter((metric) => chosen.includes(metric.key));
    // A licence that closed since the choice was made can empty the strip;
    // fall back to whatever of the default set survives rather than nothing.
    if (picked.length > 0) return picked;
    return catalogue.filter((metric) => DEFAULT_GLANCE_KEYS.includes(metric.key));
  }, [catalogue, chosen]);

  // Measured against the WIDGET, not the window - see `.widget-metrics` in
  // index.css. A quarter-width widget on a wide monitor gets one column.
  const gridCols = clsx(
    'widget-metrics',
    visibleMetrics.length === 3 && 'widget-metrics-3',
    visibleMetrics.length === 4 && 'widget-metrics-4',
    visibleMetrics.length > 4 && visibleMetrics.length <= 6 && 'widget-metrics-6',
    visibleMetrics.length > 6 && 'widget-metrics-8'
  );

  return (
    <GlassCard
      icon={<PiGaugeLight />}
      title={t('At a glance')}
      padding="none"
      className="overflow-hidden"
      fill
      bodyClassName="flex min-h-0 flex-1 flex-col"
      data-testid="quick-metrics"
      actions={
        <div className="relative" ref={picker}>
          <button
            type="button"
            data-testid="quick-metrics-picker"
            aria-haspopup="dialog"
            aria-expanded={picking}
            aria-label={t('Choose which metrics appear')}
            title={t('Choose which metrics appear')}
            onClick={() => setPicking((current) => !current)}
            className="rounded-md p-1 text-gray-400 transition-colors duration-200 ease-slide hover:text-champagne focus:text-champagne focus:outline-none focus:ring-1 focus:ring-champagne/60 dark:text-gray-500"
          >
            <PiSlidersHorizontalLight className="h-4 w-4" />
          </button>

          {picking && (
            <div
              role="dialog"
              aria-label={t('Choose which metrics appear')}
              data-testid="quick-metrics-picker-panel"
              className="glass absolute end-0 top-full z-40 mt-2 w-64 rounded-lg p-2 shadow-sm"
            >
              <p className="px-1 pb-1 text-[11px] font-medium uppercase tracking-wide text-fg-muted">
                {t('Show')}
              </p>
              <div className="custom-scrollbar-main max-h-72 overflow-y-auto">
                {catalogue.map((metric) => {
                  const on = visibleMetrics.some((m) => m.key === metric.key);
                  return (
                    <button
                      key={metric.key}
                      type="button"
                      role="switch"
                      aria-checked={on}
                      data-testid={`quick-metrics-option-${metric.key}`}
                      onClick={() => toggle(metric.key)}
                      className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-start text-sm text-fg-body transition-colors duration-200 ease-slide hover:bg-surface-sunken/70 focus:bg-surface-sunken/70 focus:outline-none"
                    >
                      <span
                        aria-hidden
                        className={clsx(
                          'flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border',
                          on
                            ? 'border-champagne bg-champagne/25 text-fg-strong'
                            : 'border-hairline-soft'
                        )}
                      >
                        {on && <PiCheckLight className="h-3 w-3" />}
                      </span>
                      <span className="truncate">{t(metric.label)}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      }
    >
      {!overview ? (
        <Skeleton variant="kpi" />
      ) : (
        /*
          `auto-rows-fr` + `h-full` is what kills the empty half: the tiles
          share the card's height between them however many rows they wrap
          onto, instead of sitting in one short band at the top.
        */
        <div
          className={clsx(
            gridCols,
            /*
              SPACING, not rules. These four tiles were separated by
              `divide-x divide-y`, which drew a full cross through the card
              and made the inside read as a little table of boxes sitting in
              a bigger box. The metrics are one panel; a gap is enough to
              tell them apart, and the card's own edge is the only line the
              eye needs.
            */
            'h-full min-h-0 auto-rows-fr gap-1 p-1'
          )}
        >
          {visibleMetrics.map((metric) => {
            const dynamic = metric.dynamicHint?.(overview) ?? null;
            return (
              <button
                key={metric.key}
                type="button"
                onClick={() => history.push(metric.route)}
                className="rounded-xl text-start transition-colors duration-200 ease-slide hover:bg-surface-sunken/60 focus:bg-surface-sunken/60 focus:outline-none"
              >
                <KpiTile
                  className="h-full"
                  icon={metric.icon}
                  value={metric.value(overview).toLocaleString()}
                  label={t(metric.label)}
                  hint={
                    dynamic
                      ? String(t(dynamic.key, { count: dynamic.count }))
                      : String(t(metric.hint))
                  }
                  tone={metric.tone?.(overview)}
                />
              </button>
            );
          })}
        </div>
      )}
    </GlassCard>
  );
};

export default QuickMetrics;
